import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { ERROR_MESSAGES } from '@common/constants';
import TeletLogo from 'src/assets/logo/tellet-logo-main.svg';
import Image from '@/components/atoms/Image';
import Routes from '@/router/routes';
import axiosInstanceNoAuth from '@/axios/axiosInstanceNoAuth';
import { EStatusCode } from '@/enums';
import { buttonVariants } from '@/components/ui/button';
import VerifyEmailModalScreen from './verify-email-modal-screen/VerifyEmailModalScreen';

// TELV1-278 F3: backend B7 throws this code when refusing verify_email for a
// half-state user. We match on the code (not the message string) so backend
// copy changes don't break this branch.
const PROFILE_INCOMPLETE_CODE = 'PROFILE_INCOMPLETE';

// Backend error shape — message-only for legacy paths; B7/B12 also include
// `code`, `redirect_to`, and `email` for routing decisions.
type ErrorResponse = {
  message?: string;
  code?: string;
  redirect_to?: string;
  email?: string;
};

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const tokenURL = searchParams.get('token');
  const [isVerified, setIsVerified] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  // TELV1-278 F3: separate state for the new chokepoint — distinct from
  // alreadyVerified / invalidLink so we can render a tailored screen.
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [healEmail, setHealEmail] = useState<string | null>(null);
  const inviterEmail = searchParams.get('inviter') || '';
  const navigate = useNavigate();

  // TELV1-278 F3: signature now includes the full error body so the 403
  // handler can read `code` and `email` from the structured payload.
  const errorStatusHandler: Record<number, (errorBody?: ErrorResponse) => void> = {
    // 401 can lead to different scenarios, so we handle it specifically
    // token expired or unauthorized
    [StatusCodes.UNAUTHORIZED]: (errorBody) => {
      const errorMessage = errorBody?.message;
      if (errorMessage === ERROR_MESSAGES.TOKEN_EXPIRED) {
        navigate(
          `${Routes.verification_email_link_expired.path}?inviter=${encodeURIComponent(
            inviterEmail,
          )}`,
        );
      }
      if (errorMessage === ERROR_MESSAGES.UNAUTHORIZED) {
        setInvalidLink(true);
      }

      setServerError(false);
    },
    // TELV1-278 B7 chokepoint: profile incomplete → route user to forgot-
    // password where they can heal in one step (B5/B5b backend + F1
    // frontend will collect their profile data alongside the password).
    [StatusCodes.FORBIDDEN]: (errorBody) => {
      if (errorBody?.code === PROFILE_INCOMPLETE_CODE) {
        setServerError(false);
        setProfileIncomplete(true);
        if (errorBody.email) setHealEmail(errorBody.email);
      } else {
        setServerError(true);
      }
    },
    [StatusCodes.CONFLICT]: () => {
      setServerError(false);
      setAlreadyVerified(true);
    },
  };

  // eslint-disable-next-line consistent-return
  const verifyToken = async () => {
    try {
      const response = await axiosInstanceNoAuth.post('/users/verify_email', undefined, {
        headers: {
          Authorization: `Bearer ${tokenURL}`,
        },
      });

      if (response.status === EStatusCode.CREATED) {
        setIsVerified(true);
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      const statusCode = axiosError?.status;
      const errorBody = axiosError?.data;

      if (!statusCode) {
        setServerError(true);
        return;
      }
      (errorStatusHandler[statusCode] || (() => setServerError(true)))(errorBody);
    }
  };

  useEffect(() => {
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isVerifiedScreen = isVerified && !serverError && !alreadyVerified && !profileIncomplete;
  const isAlreadyVerifiedScreen =
    !isVerified && !serverError && alreadyVerified && !invalidLink && !profileIncomplete;
  const isInvalidLinkScreen = !isVerified && !serverError && invalidLink && !profileIncomplete;
  const isProfileIncompleteScreen = !isVerified && !serverError && profileIncomplete;

  const resetLink = healEmail
    ? `${Routes.forgot_password.path}?email=${encodeURIComponent(healEmail)}`
    : Routes.forgot_password.path;

  return (
    <main className="flex items-center justify-center w-full h-full min-h-screen text-center bg-light-pink">
      <div className="w-full max-w-[600px] min-h-screen center border-0 px-2 py-4 min450:bg-white min450:max-w-full">
        <div className="flex flex-col items-center w-full max-w-[600px] rounded-md h-fit p-7 min450:p-5">
          <div
            className={`flex flex-col items-center ${
              isInvalidLinkScreen ? 'min-h-[240px]' : 'min-h-[350px]'
            } w-full ring-1 ring-neutral-200 max-w-[500px]
           min450:ring-transparent min450:shadow-none ${
             isInvalidLinkScreen ? 'gap-9' : 'gap-10'
           } bg-white rounded-md shadow-md h-fit p-7 min450:p-2`}
          >
            <Image effect="blur" src={TeletLogo} alt="logo" width={115} height={37} />

            {isVerifiedScreen && (
              <VerifyEmailModalScreen title="Successfully verified" linkText="Enter dashboard">
                Your email has been verified successfully.
                <br />
                You can enter the dashboard.
              </VerifyEmailModalScreen>
            )}
            {isAlreadyVerifiedScreen && (
              <VerifyEmailModalScreen title="Email already verified">
                Your email is already verified.
              </VerifyEmailModalScreen>
            )}
            {isInvalidLinkScreen && (
              <VerifyEmailModalScreen title="Invalid email confirmation link" hasLoginLink={false}>
                Please contact support at{' '}
                <a href="mailto:support@tellet.ai" className="text-primary underline">
                  support@tellet.ai
                </a>{' '}
                for assistance.
              </VerifyEmailModalScreen>
            )}
            {isProfileIncompleteScreen && (
              <>
                <h1 className="text-xl font-bold">Complete your account setup</h1>
                <p className="text-[#5a6a85] leading-6">
                  Your account needs a few more details before it can be activated. Please reset
                  your password to finish setting up your account — we&apos;ll ask for your name and
                  password in one step.
                </p>
                <Link
                  className={buttonVariants({
                    variant: 'default',
                    className: 'mt-5 transition-all ease-in-out hover:!bg-darker-primary',
                  })}
                  to={resetLink}
                >
                  Reset your password
                </Link>
              </>
            )}
            {serverError && (
              <VerifyEmailModalScreen title="Error occurred">
                An error occurred while verifying your email.
                <br />
                Please try again later.
              </VerifyEmailModalScreen>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default VerifyEmail;
